/**
 * DocViewSchema for Item Price DocType
 * 
 * Schema-driven layout for Item Price detail pages.
 * 
 * Tab structure:
 * - Details: Basic pricing information, validity, and context
 * - Meta: System/audit fields
 * 
 * This schema replaces the legacy FormViewConfig approach.
 * All layout changes should be made in this file.
 */

import type { DocViewSchema } from "./types";

export const itemPriceDocViewSchema: DocViewSchema = {
  doctype: "Item Price",
  views: {
    experience: {
      style: "shopify",
      
      // All sections available in this view
      sections: [
        // ===== Details Tab Sections =====
        {
          id: "basic",
          label: "Basic Information",
          description: "Item price identification and pricing details",
          layout: "two-column",
          fields: [
            "name",
            "item_code",
            "item_name",
            "price_list",
            "currency",
            "price_list_rate",
            "uom",
            "selling",
            "buying",
          ],
        },
        {
          id: "validity",
          label: "Validity",
          description: "Validity period and context",
          layout: "two-column",
          fields: [
            "valid_from",
            "valid_upto",
            "customer",
            "supplier",
            "batch_no",
            "packing_unit",
          ],
        },
        
        // ===== Meta Tab Sections =====
        {
          id: "audit",
          label: "System Information",
          description: "Audit and system fields",
          layout: "two-column",
          fields: [
            "owner",
            "creation",
            "modified_by",
            "modified",
          ],
        },
      ],
      
      // Tab definitions - organize sections into navigation
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["basic", "validity"],
        },
        {
          id: "meta",
          label: "Meta",
          sectionIds: ["audit"],
        },
      ],
      
      // Quick actions available in header/toolbar
      quickActions: [
        "quick_create_item_price",
        // Future actions:
        // "quick_duplicate_item_price",
        // "quick_bulk_update",
      ],
      
      // Connection groups for Connections tab
      // TODO: Wire these to connections tab when ready
      connectionGroups: [
        "pricing",
        // Future: "items", "price_lists"
      ],
    },

    // Create view: optimized for new item price creation
    // Simplified layout with one-column for full-width fields
    create: {
      style: "shopify",

      sections: [
        {
          id: "basic",
          label: "Basic Information",
          description: "Item price identification and pricing details",
          layout: "one-column",
          fields: [
            "name",
            "item_code",
            "item_name",
            "price_list",
            "currency",
            "price_list_rate",
            "uom",
            "selling",
            "buying",
          ],
        },
        {
          id: "validity",
          label: "Validity",
          description: "Validity period and context",
          layout: "one-column",
          fields: [
            "valid_from",
            "valid_upto",
            "customer",
            "supplier",
            "batch_no",
            "packing_unit",
          ],
        },
      ],

      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["basic", "validity"],
        },
      ],

      // No quick actions in create mode (item price doesn't exist yet)
      quickActions: [],

      // No connection groups in create mode
      connectionGroups: [],
    },
    
    // System view: raw/admin layout, field-focused
    system: {
      sections: [
        {
          id: "basic",
          label: "Basic",
          layout: "two-column",
          fields: [
            "name",
            "item_code",
            "item_name",
            "price_list",
            "currency",
            "price_list_rate",
            "uom",
            "selling",
            "buying",
          ],
        },
        {
          id: "validity",
          label: "Validity",
          layout: "two-column",
          fields: [
            "valid_from",
            "valid_upto",
            "customer",
            "supplier",
            "batch_no",
            "packing_unit",
          ],
        },
        {
          id: "audit",
          label: "System Information",
          layout: "two-column",
          fields: [
            "owner",
            "creation",
            "modified_by",
            "modified",
          ],
        },
      ],
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["basic", "validity"],
        },
        {
          id: "meta",
          label: "Meta",
          sectionIds: ["audit"],
        },
      ],
    },
  },
};

